import { useState, useCallback } from 'react';
import FormConfigService from '../../services/FormConfigService';

/**
 * Hook that fetches the FormConfig for a selected event (booking)
 * and provides helpers to check field visibility / required status.
 *
 * Usage inside BusDataFormWizard:
 *   const { loadConfig, isFieldVisible, isFieldRequired, configLoaded } = useFormConfig();
 *   // When event changes:
 *   await loadConfig(bookingId);
 *   // In step components:
 *   {isFieldVisible('phoneNumber') && <PhoneField required={isFieldRequired('phoneNumber')} />}
 */
export default function useFormConfig() {
  // Shown even when an older per-event config predates these fields.
  const smsConsentFallbackFields = new Set(['transactionalSmsConsent', 'marketingSmsConsent', 'aiVoiceCallConsent']);
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);

  const loadConfig = useCallback(async (bookingId) => {
    if (!bookingId) {
      // No event selected — show all fields (default behaviour)
      setConfig(null);
      setConfigLoaded(false);
      return;
    }

    setConfigLoading(true);
    try {
      const result = await FormConfigService.getPublicConfig(bookingId);
      // API returns { success, data: { sections, fields } }
      setConfig(result?.data || result);
      setConfigLoaded(true);
    } catch (err) {
      // If 404, no config exists — default to showing everything
      console.warn('No FormConfig for booking, showing all fields:', err.message);
      setConfig(null);
      setConfigLoaded(false);
    } finally {
      setConfigLoading(false);
    }
  }, []);

  /**
   * Check if a field should be visible in the onsite bus screening form.
   * If no config is loaded, all fields are visible (backward compat).
   */
  const isFieldVisible = useCallback((fieldName) => {
    if (!config?.fields) return true; // no config → show everything
    const field = config.fields.find(f => f.name === fieldName);
    if (!field) return smsConsentFallbackFields.has(fieldName);
    // Public API already filters to enabled-only fields, so just check phase
    return !field.phase || field.phase === 'onsite' || field.phase === 'both';
  }, [config]);

  /**
   * Check if a field is required.
   * Falls back to true if no config loaded (uses Formik schema).
   */
  const isFieldRequired = useCallback((fieldName) => {
    if (!config?.fields) return undefined; // let Formik schema decide
    const field = config.fields.find(f => f.name === fieldName);
    return field?.required;
  }, [config]);

  /**
   * Check if a section should be visible in the onsite form.
   */
  const isSectionVisible = useCallback((sectionKey) => {
    if (!config?.sections) return true;
    const section = config.sections.find(s => s.key === sectionKey);
    if (!section) return true;
    return section.phase === 'onsite' || section.phase === 'both';
  }, [config]);

  return {
    config,
    configLoading,
    configLoaded,
    loadConfig,
    isFieldVisible,
    isFieldRequired,
    isSectionVisible,
  };
}
